/**
 * todoStatus — iter87.5. Single source of truth for chip / modal
 * status ("live" | "open" | "locked"). Consumed by TodoChips (which
 * decides WHICH chips to show and what label they carry) AND by every
 * pop-up modal (which paints the same status pill in its header so
 * chip → modal → save always tells the same story).
 */
import {
    isGolfFormat, isGolfPickem, isSeasonFormat,
    myPicksForTournament, potPickInfo,
} from "@/lib/tournamentFormat";

export const STATUS_LABEL = { live: "Live", open: "Open", locked: "Locked", pending: "Pending" };

// iter87.43 — Locked-Chip Copy Sweep. Shared helper so every pick
// surface (SeasonPotPickPanel, KnockoutPickModal, MatchdayBoostPanel,
// GolfPickPanel) can override its "why locked" sentence with a single
// unified message when the whole tournament is done. Previously each
// surface said its own thing ("closed at kickoff", "final has kicked
// off", "matches kicked off") which was true DURING the tournament
// but confusing AFTER it ended.
export function isTournamentComplete(tournament) {
    if (!tournament) return false;
    const lc = tournament.lifecycle_state;
    return (
        lc === "final" || lc === "completed" ||
        !!tournament.completion_authorized_by || tournament.status === "archived"
    );
}

// iter87.497 (testing-agent-caught — "YOUR SEASON PICKS / POWER UP
// chips still showed LIVE and invited picking during the EPL/La Liga
// 2nd-half live-table PREVIEW") — mirrors the backend's
// `is_club_season_2h` gate (`season_pickem.season_pickem_state`):
// true while this tournament's pots are a live-table preview, not
// yet frozen by the Matchday 19 check.
export function isClubSeason2hPreview(tournament) {
    return !!(
        ["premier_league_api", "la_liga_api"].includes(tournament?.template)
        && tournament?.season_window === "second_half"
        && !tournament?.round_started?.pot_final
    );
}

// iter87.43 — Canonical "tournament is done" copy. Every locked-state
// message that used to say "kickoff" / "the final has kicked off" /
// "matches kicked off" should defer to this string when
// `isTournamentComplete(tournament)` is true.
export const TOURNAMENT_COMPLETE_LOCK_COPY = "Tournament complete — picks are locked.";

// iter87.440 — module-scope (not rebuilt per call, per code review).
const BRACKET_CHAMPION_TEMPLATES = new Set([
    "mlb_playoffs_api", "nba_playoffs_api", "nwsl_playoffs_api",
    "nfl_playoffs_api", "cfb_playoffs_api", "mls_playoffs_api", "ucl_playoffs_api",
]);
const CHAMPION_DARKHORSE_KINDS = new Set([
    "champion", "nwsl-darkhorse", "nfl-darkhorse", "cfb-darkhorse", "mls-darkhorse", "mls-playin", "ucl-darkhorse",
]);

// iter87.440 — bracket team count, template-aware: NWSL Playoffs keeps
// its 8-team roster nested at `tournament.nwsl.teams` (synced by
// `nwslplayoffs_engine.py`), not the top-level `tournament.teams`
// every other bracket template (MLB/NBA/NFL/CFB) uses. Testing agent
// caught this — without it, NWSL's Champion/Dark Horse tiles read 0
// teams and stayed gray forever even with a live 8-team field.
// iter87.465.20 — MLS Playoffs mirrors NWSL at `tournament.mls.teams`.
function bracketTeamCount(tournament) {
    if (tournament?.template === "nwsl_playoffs_api") {
        return tournament?.nwsl?.teams?.length || 0;
    }
    if (tournament?.template === "mls_playoffs_api") {
        return tournament?.mls?.teams?.length || 0;
    }
    if (tournament?.template === "ucl_playoffs_api") {
        return tournament?.ucl?.teams?.length || 0;
    }
    return tournament?.teams?.length || 0;
}

// iter87.558 (user-requested reversal of iter87.440) — NFL/CFB/NWSL
// Playoff Predictor now shows its real Open/Live color like every
// other predictor tile (MLB/NBA/UCL Series Predictor, etc.). MLS
// keeps its own forced-gray treatment here — it's still mid its
// iter87.465.24 phased gate (stays gray until Round of 16 wraps),
// unrelated to this reversal.
export function playoffPredictorDisplayStatus(tpl, status) {
    return (tpl === "mls_playoffs_api" && status !== "locked") ? "pending" : status;
}

export function todoStatusFor(kind, ctx) {
    const { tournament, me, board, boost, draftLocked } = ctx || {};
    if (!kind || !tournament) return "locked";

    // iter87.36 — Hoisted canonical lifecycle-aware lock so it applies
    // to EVERY kind, including "rank". Previously the `rank` branch
    // returned early with "live" before reaching this guard, which
    // meant a COMPLETE / FINAL World Cup KO tournament still showed
    // "Rank your top 5" as LIVE and selectable.
    //
    // The backend persists `tournament.lifecycle_state` on the ladder
    // `setup -> ready -> live -> final -> completed`. Any state at
    // "final" or beyond means every server-side write has been
    // rejecting — reflect that in the chip so players don't see
    // "Live" and try to pick / re-rank.
    //
    // iter87.199.52 — Scope the outer guard to PRE-SEASON kinds only.
    // IN-SEASON kinds (Matchday Boost, Gameday Boost, Gameday Picks,
    // Rivalries Intel) never return "locked" from the tile / modal
    // status pill because their modal is ALWAYS worth opening:
    // navigate weeks, review past picks + frozen spreads + grades.
    // The per-pick lock lives at the row level inside the modal
    // (`Locked at −1.5`), which is contextually accurate. Falling
    // through to the kind branch here gives the boost logic a chance
    // to return "open" (blue, tappable) when no more open fixtures.
    const IN_SEASON_KINDS = new Set(["boost", "rivalries-intel"]);
    const lc = tournament?.lifecycle_state;
    if (
        !IN_SEASON_KINDS.has(kind) &&
        (
            lc === "final" || lc === "completed" ||
            !!tournament?.completion_authorized_by || tournament?.status === "archived"
        )
    ) {
        return "locked";
    }

    if (kind === "rank") {
        return draftLocked ? "locked" : "live";
    }

    // iter87.440 (user-reported — "CFB has nothing...should be grayed
    // out", "NFL and NWSL have teams based on current record so I'm ok
    // with those being blue") — Champion/Dark Horse picks on a
    // bracket-playoffs template are only ever a real action once that
    // bracket's field has real teams. CFB's 12-team field is a MANUAL
    // site-admin seed step (no live ranking feed), so it can sit at
    // zero teams for days while NFL/NWSL auto-materialize from live
    // standings — gray "Pending" instead of blue "Live" until there's
    // an actual roster to pick from.
    if (
        CHAMPION_DARKHORSE_KINDS.has(kind) &&
        BRACKET_CHAMPION_TEMPLATES.has(tournament?.template) &&
        bracketTeamCount(tournament) === 0
    ) {
        return "pending";
    }

    const isGolf = isGolfFormat(tournament);
    const isGolfPk = isGolfPickem(tournament);
    const isSeason = isSeasonFormat(tournament);

    if (kind === "pot-picks") {
        const pot = potPickInfo(tournament);
        const required = pot.picksRequired;
        const myPicks = myPicksForTournament(me, tournament) || [];
        const adminLocked = !!(isGolfPk
            ? tournament?.golf?.picks_locked_by_admin
            : tournament?.pickem?.picks_locked_by_admin);
        const golfRoundLocked = isGolfPk && !!tournament?.golf?.r1_started;
        // iter87.199.55 — Season pot pick'em (soccer / NFL / CFB) locks
        // the moment the first fixture kicks off. The backend flips
        // `tournament.round_started.group = true` inside
        // `round_started_helpers.auto_lock_group_at_first_kickoff`
        // (iter87.199.50 shared helper). Without mirroring the same
        // check here, the tile stayed OPEN even after real kickoff
        // because `picks_locked_by_admin` only fires on the admin's
        // explicit Lock in Danger Zone. Operator report on prod:
        // Arsenal's opening EPL fixture had already kicked off but
        // "YOUR SEASON PICKS" still rendered as OPEN in yellow.
        // Golf's per-round lock lives in `golfRoundLocked` above and
        // stays separate — this branch handles fixture-based sports
        // only.
        // iter87.309 (user-reported — MLB Playoffs' "Your Season
        // Picks" tile still showed OPEN after first pitch, while
        // Power Up correctly showed LOCKED) — `round_started.group`
        // is flipped by the SAME shared backend helper for BOTH
        // round-robin season pickem (`auto_lock_group_at_first_
        // kickoff`) AND bracket-playoffs pot pickem
        // (`auto_lock_group_at_bracket_kickoff`, covers MLB/NBA/NFL-PO/
        // CFB-PO). This check was scoped to `isSeason` (round-robin
        // only) and never widened to the bracket siblings, even though
        // they fire the exact same flag from the exact same helper.
        const seasonKickoffLocked = (isSeason || tournament?.pickem?.mode === "pot")
            && !!tournament?.round_started?.group;
        const locked = adminLocked || golfRoundLocked || seasonKickoffLocked || isClubSeason2hPreview(tournament);
        if (locked) return "locked";
        if (myPicks.length >= required) return "open";
        return "live";
    }

    if (kind === "champion") {
        // iter87.34 — Season Pot Pick'em templates (NFL, Super Draft)
        // that ship with `pickem.champions_pick_bonus > 0` follow the
        // SAME lock semantics as their pot picks: once the season
        // (round 1 / group stage) has kicked off, the pick locks.
        // Golf pickem still returns locked (no champion concept).
        const potChampBonus = tournament?.pickem?.champions_pick_bonus || 0;
        const isSeasonPotChamp = isSeason && potChampBonus > 0
            && tournament?.pickem?.mode === "pot";
        // iter87.309 (user-reported, same MLB Playoffs report as the
        // "Your Season Picks" fix above) — MLB/NBA Playoffs' Champions
        // Pick is a SEPARATE bonus mechanic (`mlb_champs_by_tournament`/
        // `nba_champs_by_tournament`, see the `picked` fix below from
        // iter87.264) instead of the generic `pickem.champions_pick_
        // bonus` Super Draft/NFL Season use, so `isSeasonPotChamp`
        // never covered them — they fell through to `round_started
        // .final` (World Series/Finals start), staying pickable
        // through the ENTIRE postseason instead of locking at kickoff
        // like Power Up and Season Picks. Same "predict it before you
        // know anything" intent as those two — lock on the identical
        // kickoff trigger.
        const isBracketPotChamp = ["mlb_playoffs_api", "nba_playoffs_api", "nwsl_playoffs_api", "nfl_playoffs_api", "cfb_playoffs_api", "mls_playoffs_api", "ucl_playoffs_api"].includes(tournament?.template);
        if (isGolfPk) return "locked";
        if (isSeason && !isSeasonPotChamp) return "locked";
        const finalLocked = (isSeasonPotChamp || isBracketPotChamp)
            ? !!tournament?.round_started?.group   // season/postseason kicked off
            : isGolf
                ? !!tournament?.golf?.r3_started
                : !!tournament?.round_started?.final;
        // iter87.264 (user-reported bug) — MLB/NBA Playoffs Champions
        // Pick is a SEPARATE mechanic (`mlb_champs_by_tournament`/
        // `nba_champs_by_tournament`), not the generic single-slot
        // `knockout_pick_team_id` every other Champion Bonus template
        // uses. Checking the wrong field here meant `picked` was
        // always false for MLB/NBA, so the tile/modal status pill
        // stayed "live" (blue) forever even after a pick was made —
        // it should flip to "open" (yellow) like every other template.
        // iter87.363 (user-reported, same symptom) — NWSL Playoffs'
        // Champions Pick is its own `nwsl_champion_by_tournament`
        // field too; same fix, third template.
        const picked = tournament?.template === "mlb_playoffs_api"
            ? !!(me?.mlb_champs_by_tournament || {})[tournament?.id]?.ws
            : tournament?.template === "nba_playoffs_api"
                ? !!(me?.nba_champs_by_tournament || {})[tournament?.id]?.champion
                : tournament?.template === "nwsl_playoffs_api"
                    // iter87.378 — NWSL Playoffs' Champions Pick is now a
                    // LIST too (1 or 2 picks), mirroring NFL Playoffs.
                    ? ((me?.nwsl_champion_by_tournament || {})[tournament?.id] || []).length > 0
                    // iter87.374 — NFL Playoffs: `nfl_champion_picks_by_
                    // tournament` is a LIST (1 or 2 picks), not a single id.
                    : tournament?.template === "nfl_playoffs_api"
                        ? ((me?.nfl_champion_picks_by_tournament || {})[tournament?.id] || []).length > 0
                        // iter87.437 — CFB Playoffs National Champion (list, 1-2 picks).
                        : tournament?.template === "cfb_playoffs_api"
                            ? ((me?.cfb_champion_picks_by_tournament || {})[tournament?.id] || []).length > 0
                            // iter87.465.20 — MLS Champions Pick (list, 1-2 picks).
                            : tournament?.template === "mls_playoffs_api"
                                ? ((me?.mls_champion_by_tournament || {})[tournament?.id] || []).length > 0
                                // iter87.466.1 — UCL Champion (list, 1-2 picks).
                                : tournament?.template === "ucl_playoffs_api"
                                    ? ((me?.ucl_champion_by_tournament || {})[tournament?.id] || []).length > 0
                                    : !!me?.knockout_pick_team_id;
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    // iter87.363 (user-reported — "the tile stays live instead of
    // yellow open" once a Dark Horse Pick is made) — separate `kind`
    // from "champion" above because NWSL's Champions Pick and Dark
    // Horse Pick are two INDEPENDENT fields that lock at the exact
    // same instant (Round 1 kickoff) but can be picked at different
    // times — reusing the "champion" kind's single `picked` check
    // couldn't tell the two apart.
    if (kind === "nwsl-darkhorse") {
        const finalLocked = !!tournament?.round_started?.group;
        const picked = !!(me?.nwsl_darkhorse_by_tournament || {})[tournament?.id];
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    // iter87.374 — sibling of "nwsl-darkhorse" above, but NFL has TWO
    // independent conference slots (AFC + NFC) — "picked" here means
    // at least one of the two has been set (so the tile flips to
    // "open" the moment either slot is filled, same philosophy as
    // every other pre-season tile).
    if (kind === "nfl-darkhorse") {
        const finalLocked = !!tournament?.round_started?.group;
        const dh = (me?.nfl_darkhorse_by_tournament || {})[tournament?.id] || {};
        const picked = !!dh.AFC || !!dh.NFC;
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    // iter87.437 — CFB Playoffs single Dark Horse Pick (seed 5-12),
    // locks with National Champion at First Round kickoff.
    if (kind === "cfb-darkhorse") {
        const finalLocked = !!tournament?.round_started?.group;
        const picked = !!(me?.cfb_darkhorse_by_tournament || {})[tournament?.id];
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    // iter87.465.20 — MLS Playoffs Dark Horse Pick, sibling of
    // "nwsl-darkhorse" above. Locks with Champions Pick/Play-In Pick
    // at Wild Card kickoff.
    if (kind === "mls-darkhorse") {
        const finalLocked = !!tournament?.round_started?.group;
        const picked = !!(me?.mls_darkhorse_by_tournament || {})[tournament?.id];
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    // iter87.465.20 — MLS Playoffs Play-In Pick (new primitive) — 2
    // independent conference slots (East/West), same lock instant as
    // Champions/Dark Horse. "Picked" means at least one slot is filled.
    if (kind === "mls-playin") {
        const finalLocked = !!tournament?.round_started?.group;
        const pi = (me?.mls_playin_by_tournament || {})[tournament?.id] || {};
        const picked = !!pi.East || !!pi.West;
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    // iter87.466.1 — UCL Playoffs Dark Horse Pick, sibling of
    // "mls-darkhorse" above. Locks with UCL Champion at Knockout Round
    // Play-offs kickoff.
    if (kind === "ucl-darkhorse") {
        const finalLocked = !!tournament?.round_started?.group;
        const picked = !!(me?.ucl_darkhorse_by_tournament || {})[tournament?.id];
        if (finalLocked) return "locked";
        if (picked) return "open";
        return "live";
    }

    if (kind === "power") {
        const myRow = board?.find?.((r) => r.participant_id === me?.id);
        const golfLocked = isGolf && !!(tournament?.golf?.r1_started || tournament?.golf?.picks_locked_by_admin);
        // iter87.199.187 — Operator report: an EPL tournament with
        // Season Picks already LOCKED (season kicked off) still showed
        // Power Up as LIVE/pickable for a crewmate who never made a
        // power pick before kickoff. Root cause: `lock_power_picks_
        // for_tournament` only snapshots a multiplier for participants
        // who actually picked a team (no pick = nothing to snapshot),
        // so `myRow.power_pick_multiplier` alone can never detect "the
        // window has closed" for someone who skipped picking entirely.
        // Mirror the SAME season-kickoff signal `pot-picks`/`champion`
        // already use, so Power Up locks in lockstep with Season
        // Picks regardless of whether this participant ever picked.
        // iter87.405 (user-reported, found via testing while fixing a
        // sibling bug) — this only covered `isSeason` (round-robin),
        // never widened to bracket-playoffs pot pickem (MLB/NBA/NFL-PO/
        // CFB-PO) the way `pot-picks`/`champion` already were (iter87.
        // 309/264) — their Power Up tile never locked via kickoff at
        // all, only via `myRow.power_pick_multiplier` (which, same gap
        // as above, is never set for a non-picker), so it stayed LIVE
        // indefinitely post-lock for anyone who skipped picking.
        const seasonKickoffLocked = (isSeason || tournament?.pickem?.mode === "pot")
            && !!tournament?.round_started?.group;
        const seasonLocked = seasonKickoffLocked || !!myRow?.power_pick_multiplier;
        const locked = isGolf ? golfLocked : (seasonLocked || isClubSeason2hPreview(tournament));
        const pickTeamId = isGolf ? myRow?.power_pick_team_id : me?.power_pick_team_id;
        const picked = !!pickTeamId;
        if (locked) {
            // iter87.278.2 (user-requested) — a pot pick'em Power Pick
            // that got auto-cleared because its team dropped out of
            // the field (`season_pickem.reconcile_dropped_pot_picks`)
            // can be re-picked from the player's remaining Season
            // Picks roster even though the tournament is otherwise
            // locked (`power_pick.py`'s matching lock carve-out).
            // iter87.405 (user-reported) — that carve-out ONLY matches
            // a team already in the player's own Season Picks — if
            // they made zero Season Picks (or all of them are now
            // eliminated/missed the field), there's nothing eligible
            // to swap into, and the modal correctly shows "Lock in
            // your club picks first". The tile must agree: only flip
            // to "live" when at least one Season Pick is still alive
            // and in the field.
            const isPotMode = tournament?.pickem?.mode === "pot";
            if (isPotMode && !picked) {
                const teamsById = Object.fromEntries((tournament?.teams || []).map((tm) => [tm.id, tm]));
                const hasSwappableRoster = myPicksForTournament(me, tournament)
                    .some((tid) => { const tm = teamsById[tid]; return tm && !tm.eliminated && !tm.missed_playoffs; });
                if (hasSwappableRoster) return "live";
            }
            return "locked";
        }
        if (picked) return "open";
        return "live";
    }

    if (kind === "boost") {
        const st = boost?.state;
        // iter87.199.163 — Mid-season "Start Tournament" gate: picks
        // 403 server-side until the admin hits Start, so the tile
        // must never claim LIVE/OPEN while `pending_start` is true.
        if (st?.pending_start) return "locked";
        const cap = st?.picks_per_round || 2;
        const fixtures = st?.fixtures || [];
        const myPicks = st?.my_picks || [];
        const openFixtures = fixtures.filter((f) => !f.started);
        // iter87.199.52 — Boost never returns "locked". If no more open
        // fixtures in the current week (or the tournament is complete),
        // the modal is still worth opening to review past picks + grades
        // + frozen spreads, so the tile stays "open" (blue border /
        // OPEN pill / tappable). Individual per-pick lock states are
        // handled inside the modal, not at the tile / modal-header
        // level. Operator directive iter87.199.51-52.
        if (openFixtures.length === 0) return "open";
        return myPicks.length < cap ? "live" : "open";
    }

    return "locked";
}
